import { Box, CardContent, Link, List, ListItem, ListItemText, Typography } from "@mui/material";
import { FC } from "react";
import { Link as LinkBase } from "react-router-dom";
import { Requirement } from "types/requirement";
import { REQUIREMENT } from "helpers/constants";
import { ROLES } from "types/user";
import { CardOutline } from "components/Card";
import { RequirementFeedbackStatsPanelCompacted } from "components/Analytics/RequirementFeedbackStatsPanelCompacted";
import { CompetencyRatingsChart } from "components/Charts";

type EPACompetencyRequirementsItemProps = {
  item: Requirement;
  userRole: string;
  skillLevel?: number;
  feedbackStats?: any;
  onClickDot: (data: any) => void;
  isIter?: boolean;
  iterSubRequirements?: Array<{
    requirement_id: string;
    name: string;
    description?: string | null;
  }>;
  iterRatingsByRequirement?: Record<
    string,
    {
      date: string;
      learnerRating?: number | null;
      coachRating?: number | null;
    }[]
  >;
};

export const EPACompetencyRequirementsItem: FC<
  EPACompetencyRequirementsItemProps
> = ({
  item,
  userRole,
  feedbackStats,
  onClickDot,
  isIter,
  iterSubRequirements = [],
  iterRatingsByRequirement = {},
}) => {
  return (
    <CardOutline
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography
          variant="body1"
          fontWeight={600}
          mb={1}
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.name}
        </Typography>
        <Typography
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.description}
        </Typography>
      </CardContent>

      <Box
        mt={0}
        sx={{ padding: "1rem", color: "text.secondary", fontSize: "0.9rem" }}
      >
        {isIter && iterSubRequirements.length > 0 ? (
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Competency progress
            </Typography>
            <List disablePadding sx={{ mt: 0 }}>
              {iterSubRequirements.map((subReq) => (
                <ListItem
                  key={subReq.requirement_id}
                  disablePadding
                  sx={{ flexDirection: "column", alignItems: "flex-start", mb: 1.5 }}
                >
                  <ListItemText
                    primary={subReq.name}
                    secondary={subReq.description || null}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                  <Box mt={0.5} width="100%">
                    {iterRatingsByRequirement[subReq.requirement_id] &&
                    iterRatingsByRequirement[subReq.requirement_id].length > 0 ? (
                      <CompetencyRatingsChart
                        data={iterRatingsByRequirement[subReq.requirement_id]}
                        height={120}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No ratings data yet
                      </Typography>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          userRole === ROLES.STUDENT &&
          feedbackStats &&
          feedbackStats?.totalFeedbacks > 0 && (
            <RequirementFeedbackStatsPanelCompacted
              totalFeedbacks={feedbackStats?.totalFeedbacks}
              currentRate={feedbackStats?.currentRate}
              data={feedbackStats?.data}
              onClickDot={() => {
                onClickDot(item);
              }}
            />
          )
        )}
      </Box>

      <Box
        mt={0}
        sx={{
          padding: "1rem",
          color: "text.secondary",
          fontSize: "0.9rem",
          textAlign: "right",
        }}
      >
        <Link
          component={LinkBase}
          to={`/requirements/${
            item.type === REQUIREMENT.REQUIREMENT_COMPETENCY
              ? "competency"
              : item.type === REQUIREMENT.REQUIREMENT_ITER
                ? "iter"
                : "epa"
          }/${item.requirement_id}`}
        >
          View More
        </Link>
      </Box>
    </CardOutline>
  );
};
